import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Collapse, Form, Input, InputNumber, Modal, Switch, Table, Tag, Tooltip } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { ArrowForward, CheckCircleOutline, RadioButtonUnchecked } from '@material-ui/icons';
import { SyncOutlined, WarningOutlined } from '@ant-design/icons';
import toast from 'components/Basic/Toast';
import Loading from 'components/Basic/Loading';
import {
  useGetIntegrationQuery,
  useGetCatalogSyncStatusQuery,
  useGetConversionEventsConfigQuery,
  useGetConversionEventsStatusQuery,
  useToggleIntegrationMutation,
  useTriggerCatalogSyncMutation,
  useUpdateIntegrationChannelMutation,
  useUpdateIntegrationMutation,
} from 'core/integrations/IntegrationsService';
import {
  ChannelHealth,
  MetaCapiSettings,
  MetaCatalogSettings,
  MetaChannel,
  MetaTokenKey,
  MetaTokenSource,
  META_SLUG,
  META_TOKEN_KEYS,
  isMasked,
  maskedKeysOf,
  metaTokenSourceOf,
  withoutMaskedValues,
} from 'core/integrations/types';

const PROVIDER = 'meta';

const DEFAULT_CATALOG: MetaCatalogSettings = {
  catalogId: '',
  imageBaseUrl: '',
  productLinkBaseUrl: '',
  syncIntervalMinutes: 15,
  graphApiVersion: 'v21.0',
};

const DEFAULT_CAPI: MetaCapiSettings = {
  pixelId: '',
  testEventCode: '',
};

const MAX_SYNC_MINUTES = 1440;

/** Form label of each setting key the backend can report as missing. */
const SETTING_LABELS: Record<string, string> = {
  catalogId: 'Catalog ID',
  imageBaseUrl: 'Image Base URL',
  productLinkBaseUrl: 'Product Link Base URL',
  pixelId: 'Pixel ID',
};

/** The inputs hold a replacement value; empty means "keep the current token". */
const EMPTY_TOKENS: Record<MetaTokenKey, string> = { [META_TOKEN_KEYS.catalog]: '', [META_TOKEN_KEYS.capi]: '' };

/** What the admin typed, ready to send; the mask typed by hand counts as nothing. */
const typedToken = (value: string): string => {
  const trimmed = value.trim();
  return isMasked(trimmed) ? '' : trimmed;
};

interface TokenInfo {
  source: MetaTokenSource;
}

const tokenInfoOf = (value: unknown): TokenInfo => ({ source: metaTokenSourceOf(value) });

const MISSING_TOKENS: Record<MetaTokenKey, TokenInfo> = {
  [META_TOKEN_KEYS.catalog]: { source: 'missing' },
  [META_TOKEN_KEYS.capi]: { source: 'missing' },
};

/**
 * The backend guards catalog syncs with a lock shared between the cron and the
 * manual trigger, and answers `409` when it is already held. That is an expected
 * outcome — nothing broke, it just was not the moment — so it is reported as a
 * notice and the buttons rest briefly instead of inviting a retry loop.
 */
const SYNC_CONFLICT_STATUS = 409;
const SYNC_COOLDOWN_MS = 8000;

const isSyncConflict = (error: unknown): boolean =>
  (error as { status?: number } | null)?.status === SYNC_CONFLICT_STATUS;

/** The backend's own wording for an error, shown untranslated when present. */
const backendMessageOf = (error: unknown): string => {
  const message = (error as { data?: { message?: unknown } } | null)?.data?.message;
  return typeof message === 'string' ? message : '';
};

/** Meta Graph API versions look like `v21.0`; the backend puts this in a URL path. */
const GRAPH_API_VERSION_PATTERN = /^v\d+\.\d+$/;

const isHttpsUrl = (value: string): boolean => {
  if (!value) return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const MetaConfig: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });

  // Master switch (integration level).
  const [enabled, setEnabled] = useState(false);
  const [initialEnabled, setInitialEnabled] = useState(false);

  // Catalog channel.
  const [catalogEnabled, setCatalogEnabled] = useState(false);
  const [catalog, setCatalog] = useState<MetaCatalogSettings>(DEFAULT_CATALOG);
  const [initialCatalogEnabled, setInitialCatalogEnabled] = useState(false);
  const [initialCatalog, setInitialCatalog] = useState<MetaCatalogSettings>(DEFAULT_CATALOG);

  // Conversions API channel.
  const [capiEnabled, setCapiEnabled] = useState(false);
  const [capi, setCapi] = useState<MetaCapiSettings>(DEFAULT_CAPI);
  const [initialCapiEnabled, setInitialCapiEnabled] = useState(false);
  const [initialCapi, setInitialCapi] = useState<MetaCapiSettings>(DEFAULT_CAPI);

  // Settings exactly as the server returned them (masked keys excluded), so keys
  // this panel does not model survive the wholesale channel PUT.
  const [serverSettings, setServerSettings] = useState<Record<MetaChannel, Record<string, unknown>>>({
    catalog: {},
    capi: {},
  });
  const [maskedKeys, setMaskedKeys] = useState<Record<MetaChannel, string[]>>({ catalog: [], capi: [] });

  // Access tokens (integration level, stored encrypted). The API never returns
  // them, only how each one is currently provided.
  const [tokens, setTokens] = useState<Record<MetaTokenKey, string>>(EMPTY_TOKENS);
  const [tokenInfo, setTokenInfo] = useState<Record<MetaTokenKey, TokenInfo>>(MISSING_TOKENS);

  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncCooldown, setSyncCooldown] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  // Subscribed queries: their cached data is what the component renders, and tag
  // invalidation from the mutations below refetches them automatically. Reading a
  // lazy trigger's return value instead would yield the *previous* cached value,
  // leaving the status badges one save behind.
  const {
    data: integration,
    isFetching: isFetchingIntegration,
    isError: integrationFailed,
    refetch: refetchIntegration,
  } = useGetIntegrationQuery({ slug: META_SLUG }, { refetchOnMountOrArgChange: true });
  const { data: syncStatus } = useGetCatalogSyncStatusQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data: publicConfig } = useGetConversionEventsConfigQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data: capiStatus } = useGetConversionEventsStatusQuery(undefined, { refetchOnMountOrArgChange: true });

  const [updateChannel] = useUpdateIntegrationChannelMutation();
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [toggleIntegration] = useToggleIntegrationMutation();
  const [triggerCatalogSync] = useTriggerCatalogSyncMutation();

  const catalogDirty =
    catalogEnabled !== initialCatalogEnabled || JSON.stringify(catalog) !== JSON.stringify(initialCatalog);
  const capiDirty = capiEnabled !== initialCapiEnabled || JSON.stringify(capi) !== JSON.stringify(initialCapi);
  const masterDirty = enabled !== initialEnabled;
  const catalogTokenDirty = typedToken(tokens[META_TOKEN_KEYS.catalog]) !== '';
  const capiTokenDirty = typedToken(tokens[META_TOKEN_KEYS.capi]) !== '';
  const tokensDirty = catalogTokenDirty || capiTokenDirty;
  const isDirty = catalogDirty || capiDirty || masterDirty || tokensDirty;

  // Seeds the form from the server payload: on load and on every refetch, so the
  // freshest payload always wins — but never while the user has edits in
  // progress, nor while a refetch is in flight: right after a save the cache still
  // holds the previous payload, and seeding from it would briefly undo the save.
  useEffect(() => {
    if (!integration || isDirty || isFetchingIntegration) return;

    setEnabled(integration.enabled);
    setInitialEnabled(integration.enabled);

    const rawSettings = (integration.settings ?? {}) as Record<string, unknown>;
    setTokenInfo({
      [META_TOKEN_KEYS.catalog]: tokenInfoOf(rawSettings[META_TOKEN_KEYS.catalog]),
      [META_TOKEN_KEYS.capi]: tokenInfoOf(rawSettings[META_TOKEN_KEYS.capi]),
    });

    const channels = Array.isArray(integration.channels) ? integration.channels : [];
    const catalogChannel = channels.find((c: any) => c.channel === 'catalog');
    const capiChannel = channels.find((c: any) => c.channel === 'capi');

    // Masked ("***") values are placeholders, never real data — keep them out of
    // the form so they are not echoed back on save. Which keys were masked is
    // remembered separately, because saving over them would destroy the value.
    const rawCatalog = (catalogChannel?.settings ?? {}) as Record<string, unknown>;
    const rawCapi = (capiChannel?.settings ?? {}) as Record<string, unknown>;
    setMaskedKeys({ catalog: maskedKeysOf(rawCatalog), capi: maskedKeysOf(rawCapi) });

    // The channel PUT replaces the settings object wholesale, so the untouched
    // server keys are retained here and re-sent on save; otherwise any setting
    // this panel does not model yet would be silently dropped from the database.
    const cs = withoutMaskedValues(rawCatalog) as any;
    const ps = withoutMaskedValues(rawCapi) as any;
    setServerSettings({ catalog: cs, capi: ps });

    const loadedCatalog: MetaCatalogSettings = {
      catalogId: cs.catalogId ?? '',
      imageBaseUrl: cs.imageBaseUrl ?? '',
      productLinkBaseUrl: cs.productLinkBaseUrl ?? '',
      syncIntervalMinutes: cs.syncIntervalMinutes ?? DEFAULT_CATALOG.syncIntervalMinutes,
      graphApiVersion: cs.graphApiVersion ?? DEFAULT_CATALOG.graphApiVersion,
    };
    setCatalog(loadedCatalog);
    setInitialCatalog(loadedCatalog);
    setCatalogEnabled(catalogChannel?.enabled ?? false);
    setInitialCatalogEnabled(catalogChannel?.enabled ?? false);

    const loadedCapi: MetaCapiSettings = {
      pixelId: ps.pixelId ?? '',
      testEventCode: ps.testEventCode ?? '',
    };
    setCapi(loadedCapi);
    setInitialCapi(loadedCapi);
    setCapiEnabled(capiChannel?.enabled ?? false);
    setInitialCapiEnabled(capiChannel?.enabled ?? false);
  }, [integration, isDirty, isFetchingIntegration]);

  const isLoading = isFetchingIntegration || isSaving;

  const handleCatalogChange = <K extends keyof MetaCatalogSettings>(field: K, value: MetaCatalogSettings[K]) =>
    setCatalog(prev => ({ ...prev, [field]: value }));

  const handleCapiChange = <K extends keyof MetaCapiSettings>(field: K, value: MetaCapiSettings[K]) =>
    setCapi(prev => ({ ...prev, [field]: value }));

  const handleTokenChange = (key: MetaTokenKey, value: string) => setTokens(prev => ({ ...prev, [key]: value }));

  const metaStatus = syncStatus?.[PROVIDER] ?? null;
  const catalogOperating = Boolean(metaStatus?.enabled);
  const capiOperating = Boolean(capiStatus?.enabled);
  // What the web actually gets, from the same public endpoint it calls.
  const storefrontTracking = Boolean(publicConfig?.enabled);

  // A switch that is off explains itself, so nothing is shown for it; an unreadable
  // token is reported even then, because it describes the stored value.
  const channelReason = (health?: ChannelHealth | null): React.ReactNode => {
    if (!health || health.enabled) return null;
    const missingLabels = health.missing
      .filter(key => key !== META_TOKEN_KEYS.catalog && key !== META_TOKEN_KEYS.capi)
      .map(key => (key in SETTING_LABELS ? t(SETTING_LABELS[key]) : key));
    const missingTokenLabel = health.missing.some(
      key => key === META_TOKEN_KEYS.catalog || key === META_TOKEN_KEYS.capi,
    )
      ? [t('Access token')]
      : [];
    const fields = [...missingTokenLabel, ...missingLabels];
    if (!health.tokenUnreadable) {
      return fields.length ? <FormattedMessage id="Missing: {fields}" values={{ fields: fields.join(', ') }} /> : null;
    }
    return (
      <>
        <FormattedMessage id="The stored access token cannot be read. Enter it again." />
        {missingLabels.length > 0 && (
          <>
            {' '}
            <FormattedMessage id="Missing: {fields}" values={{ fields: missingLabels.join(', ') }} />
          </>
        )}
      </>
    );
  };
  const catalogReason = channelReason(metaStatus);
  const capiReason = channelReason(capiStatus);
  const tokenUnreadable = (key: MetaTokenKey): boolean =>
    Boolean(key === META_TOKEN_KEYS.catalog ? metaStatus?.tokenUnreadable : capiStatus?.tokenUnreadable);

  // The badges report live server state, so while there are unsaved edits they
  // would contradict the switch the user just flipped. Say "pending" instead of
  // showing a confident green/red that is about to change.
  const catalogPending = catalogDirty || masterDirty || catalogTokenDirty;
  const capiPending = capiDirty || masterDirty || capiTokenDirty;

  // A token with no stored value can only come from this input.
  const tokenMissing = (key: MetaTokenKey) => tokenInfo[key].source === 'missing' && !typedToken(tokens[key]);

  // Field-level validation, scoped to the fields actually on screen: a channel is
  // only rendered when both the master switch and its own switch are on, and
  // flagging an error on a hidden field would leave the user unable to act on it
  // (it would, for one, block the master kill switch).
  const errors: Record<string, string> = {};
  if (enabled && catalogEnabled) {
    if (!catalog.catalogId.trim()) errors.catalogId = t('This field is required to operate the channel.');
    if (catalog.imageBaseUrl && !isHttpsUrl(catalog.imageBaseUrl))
      errors.imageBaseUrl = t('Please enter a valid https:// URL.');
    if (catalog.productLinkBaseUrl && !isHttpsUrl(catalog.productLinkBaseUrl))
      errors.productLinkBaseUrl = t('Please enter a valid https:// URL.');
    if (
      !catalog.syncIntervalMinutes ||
      catalog.syncIntervalMinutes < 1 ||
      catalog.syncIntervalMinutes > MAX_SYNC_MINUTES
    )
      errors.syncIntervalMinutes = t('Must be a whole number between 1 and 1440.');
    // The backend interpolates this into the Meta Graph API request path, so it
    // is constrained to the documented shape rather than accepted free-form.
    if (catalog.graphApiVersion && !GRAPH_API_VERSION_PATTERN.test(catalog.graphApiVersion.trim()))
      errors.graphApiVersion = t('Use the vXX.X format, for example v21.0.');
    if (tokenMissing(META_TOKEN_KEYS.catalog))
      errors[META_TOKEN_KEYS.catalog] = t('This field is required to operate the channel.');
  }
  if (enabled && capiEnabled) {
    if (!capi.pixelId.trim()) errors.pixelId = t('This field is required to operate the channel.');
    if (tokenMissing(META_TOKEN_KEYS.capi))
      errors[META_TOKEN_KEYS.capi] = t('This field is required to operate the channel.');
  }

  const onCancel = () => {
    setEnabled(initialEnabled);
    setCatalogEnabled(initialCatalogEnabled);
    setCatalog(initialCatalog);
    setCapiEnabled(initialCapiEnabled);
    setCapi(initialCapi);
    setTokens(EMPTY_TOKENS);
  };

  const doSave = async () => {
    setIsSaving(true);
    try {
      // Each channel PUT replaces its settings object wholesale, so the payload
      // starts from everything the server sent and only overrides what this panel
      // edits — keys it does not model yet would otherwise be dropped.
      if (catalogDirty) {
        await updateChannel({
          slug: META_SLUG,
          channel: 'catalog',
          enabled: catalogEnabled,
          settings: {
            ...serverSettings.catalog,
            catalogId: catalog.catalogId.trim(),
            imageBaseUrl: catalog.imageBaseUrl.trim(),
            productLinkBaseUrl: catalog.productLinkBaseUrl.trim(),
            syncIntervalMinutes: catalog.syncIntervalMinutes,
            graphApiVersion: catalog.graphApiVersion.trim(),
          },
        }).unwrap();
        // Snapshot per channel, as each one lands: if a later request fails, this
        // channel is not left marked dirty against values already persisted.
        setInitialCatalog(catalog);
        setInitialCatalogEnabled(catalogEnabled);
      }

      if (capiDirty) {
        await updateChannel({
          slug: META_SLUG,
          channel: 'capi',
          enabled: capiEnabled,
          settings: {
            ...serverSettings.capi,
            pixelId: capi.pixelId.trim(),
            testEventCode: capi.testEventCode.trim(),
          },
        }).unwrap();
        setInitialCapi(capi);
        setInitialCapiEnabled(capiEnabled);
      }

      if (tokensDirty) {
        // Unlike the channels, the integration PUT merges key by key and encrypts
        // these, so only the tokens actually typed travel; the rest stay as stored.
        const settings = Object.fromEntries(
          Object.entries(tokens)
            .map(([key, value]) => [key, typedToken(value)])
            .filter(([, value]) => value !== ''),
        );
        await updateIntegration({ slug: META_SLUG, body: { settings } }).unwrap();
        setTokens(EMPTY_TOKENS);
        // The refetch confirms it, but the label should not wait for it.
        setTokenInfo(prev => {
          const next = { ...prev };
          Object.keys(settings).forEach(key => {
            next[key as MetaTokenKey] = { source: 'stored' };
          });
          return next;
        });
      }

      if (masterDirty) {
        await toggleIntegration({ slug: META_SLUG, enabled }).unwrap();
        setInitialEnabled(enabled);
      }

      // The mutations invalidate Integrations / CatalogSyncStatus /
      // ConversionEventsConfig, so the subscribed queries refetch on their own and
      // the seeding effect re-syncs the form once nothing is dirty.
      toast.success({ title: t('Meta configuration updated successfully!') });
    } catch {
      // Channels that already succeeded were snapshotted above; whatever is still
      // dirty keeps the user's input and is re-sent on the next save.
      toast.error({ title: t('Error updating Meta configuration') });
    } finally {
      setIsSaving(false);
    }
  };

  const onSave = () => {
    // Nothing to send: saying "saved" here would be a lie, and it would also leave
    // the screen claiming success for a request that never happened.
    if (!isDirty) {
      toast.info({ title: t('There are no changes to save.') });
      return;
    }

    if (Object.keys(errors).length > 0) {
      toast.error({ title: t('Please fix the highlighted fields before saving.') });
      return;
    }

    // A wholesale PUT cannot preserve a value the API returned masked: echoing the
    // mask would store it literally, omitting it would delete it. Stop instead of
    // destroying the real value.
    const blockedByMask = [...(catalogDirty ? maskedKeys.catalog : []), ...(capiDirty ? maskedKeys.capi : [])];
    if (blockedByMask.length > 0) {
      toast.error({
        title: intl.formatMessage(
          { id: 'Cannot save: re-enter the masked values first ({fields}).' },
          { fields: blockedByMask.join(', ') },
        ),
      });
      return;
    }

    // A test event code means no real conversion is counted — it must never be
    // saved by accident. Only warn when the CAPI channel is actually being sent.
    const testModeBeingSaved = Boolean(capi.testEventCode.trim()) && capiEnabled && capiDirty;

    Modal.confirm({
      title: t('Save changes'),
      content: testModeBeingSaved ? t('Meta test mode save warning') : t('Meta save warning'),
      okText: t('Save'),
      cancelText: t('Cancel'),
      className: 'integration_confirm_modal',
      onOk: doSave,
    });
  };

  const runSync = async (mode: 'full' | 'delta') => {
    setIsSyncing(true);
    try {
      const res = await triggerCatalogSync({ provider: PROVIDER, mode }).unwrap();
      if (!res.triggered) {
        toast.info({ title: res.reason || t('Synchronization could not be started.') });
      } else {
        const report = res.report;
        const failed = report?.itemErrors?.length ?? 0;
        if (report?.error) {
          toast.error({ title: report.error });
        } else if (failed > 0) {
          toast.info({
            title: intl.formatMessage({ id: 'Sync finished with {count} product errors.' }, { count: failed }),
          });
        } else {
          toast.success({ title: t('Catalog synchronized successfully!') });
        }
      }
      // triggerCatalogSync invalidates CatalogSyncStatus, so the report refreshes
      // on its own.
    } catch (error) {
      if (isSyncConflict(error)) {
        setSyncCooldown(true);
        toast.info({ title: backendMessageOf(error) || t('A catalog sync is already running.') });
      } else {
        toast.error({ title: t('Error synchronizing the catalog') });
      }
    } finally {
      setIsSyncing(false);
    }
  };

  // Releases the buttons once the cooldown elapses; the cleanup also covers the
  // user navigating away while it is still counting down.
  useEffect(() => {
    if (!syncCooldown) return undefined;
    const timer = setTimeout(() => setSyncCooldown(false), SYNC_COOLDOWN_MS);
    return () => clearTimeout(timer);
  }, [syncCooldown]);

  // Why the sync buttons are disabled, when they are — empty renders no tooltip.
  let syncBlockedReason = '';
  if (isDirty) syncBlockedReason = t('Save your changes before running a synchronization.');
  else if (syncCooldown) syncBlockedReason = t('Another synchronization is already running. Try again in a moment.');

  const formatDateTime = (value?: string | null) =>
    value
      ? `${intl.formatDate(value, { day: '2-digit', month: '2-digit', year: 'numeric' })} ${intl.formatTime(value)}`
      : t('Never');

  const lastReport = metaStatus?.lastReport ?? null;

  const reportDuration = useMemo(() => {
    if (!lastReport?.startedAt || !lastReport?.finishedAt) return null;
    const ms = new Date(lastReport.finishedAt).getTime() - new Date(lastReport.startedAt).getTime();
    if (Number.isNaN(ms) || ms < 0) return null;
    return `${(ms / 1000).toFixed(1)} s`;
  }, [lastReport]);

  const statusCardStyle = {
    borderColor: enabled ? '#b7eb8f' : '#ffccc7',
    background: enabled ? '#f6ffed' : '#fff2f0',
    borderRadius: 8,
  };

  const labelOf = (id: string) => (
    <span className="title" style={{ marginBottom: 0 }}>
      <FormattedMessage id={id} />
    </span>
  );

  // Where the token comes from today, next to its label: the value itself is
  // never shown, so this is the only feedback the admin has about it.
  const tokenLabel = (key: MetaTokenKey) => {
    const { source } = tokenInfo[key];
    return (
      <span className="title" style={{ marginBottom: 0, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <FormattedMessage id="Access token" />
        {source === 'stored' && tokenUnreadable(key) && (
          <Tag color="warning" style={{ margin: 0 }}>
            <FormattedMessage id="Configured but unreadable" />
          </Tag>
        )}
        {source === 'stored' && !tokenUnreadable(key) && (
          <Tag color="success" style={{ margin: 0 }}>
            <FormattedMessage id="Configured" />
          </Tag>
        )}
        {source === 'missing' && (
          <Tag color="error" style={{ margin: 0 }}>
            <FormattedMessage id="Not configured" />
          </Tag>
        )}
      </span>
    );
  };

  const tokenPlaceholder = (key: MetaTokenKey) => {
    switch (tokenInfo[key].source) {
      case 'stored':
        return t('Enter a new value to replace it');
      default:
        return t('Required to operate the channel');
    }
  };

  const channelBadge = (operating: boolean, pending: boolean, labelId: string, reason: React.ReactNode) => (
    <div className="meta_channel_badge">
      {pending ? (
        <RadioButtonUnchecked style={{ fontSize: 15, color: '#faad14' }} />
      ) : operating ? (
        <CheckCircleOutline style={{ fontSize: 15, color: '#52c41a' }} />
      ) : (
        <RadioButtonUnchecked style={{ fontSize: 15, color: '#ff4d4f' }} />
      )}
      <span className="meta_channel_badge_label">
        <FormattedMessage id={labelId} />
      </span>
      {pending ? (
        <Tag color="warning" style={{ margin: 0 }}>
          <FormattedMessage id="Pending save" />
        </Tag>
      ) : (
        <Tag color={operating ? 'success' : 'error'} style={{ margin: 0 }}>
          <FormattedMessage id={operating ? 'Operating' : 'Not operating'} />
        </Tag>
      )}
      {!pending && reason && <span className="meta_channel_badge_reason">{reason}</span>}
    </div>
  );

  const itemErrors = lastReport?.itemErrors ?? [];

  // Without the current configuration the form would render its empty defaults,
  // which look exactly like "Meta is unconfigured". Saving from that state would
  // write those blanks over the real settings, so editing is refused until the
  // configuration can actually be read. Placed after every hook, so hook order
  // stays stable across renders.
  if (integrationFailed && !integration) {
    return (
      <div className="integrations_layout meta_config">
        <Loading visible={isFetchingIntegration} />
        <Card style={{ borderColor: '#ffccc7', background: '#fff2f0', borderRadius: 8 }}>
          <p className="title" style={{ marginBottom: 4 }}>
            <FormattedMessage id="Meta configuration could not be loaded" />
          </p>
          <p className="description" style={{ marginBottom: 12 }}>
            <FormattedMessage id="The current settings could not be read, so editing is disabled to avoid overwriting them. Check your connection and try again." />
          </p>
          <Button className="btn_clear" onClick={() => refetchIntegration()} disabled={isFetchingIntegration}>
            <FormattedMessage id="Retry" />
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="integrations_layout meta_config">
      <Loading visible={isLoading} />

      <div className="integrations_status_card">
        <Card style={statusCardStyle}>
          <div className="status_row">
            <div className="status_info">
              <p className="title" style={{ marginBottom: 4 }}>
                <FormattedMessage id="Enable Meta Integration" />
              </p>
              <p className="description" style={{ marginBottom: 8 }}>
                <FormattedMessage id="Master switch for the Meta integration — turns the product catalog sync and conversion events off at once." />
              </p>
              <div className="meta_channel_badges">
                {channelBadge(catalogOperating, catalogPending, 'Product Catalog', catalogReason)}
                {channelBadge(capiOperating, capiPending, 'Conversion Events', capiReason)}
              </div>
              <p className="description meta_hint">
                <FormattedMessage id="Live server status. A channel operates only when the master switch, its own switch, its required ID and its access token are all in place." />
              </p>
            </div>
            <Switch id="integration-meta-enabled" checked={enabled} onChange={setEnabled} />
          </div>
        </Card>
      </div>

      {enabled && (
        <div className="integrations_grid">
          <div className="integrations_left_col">
            <Card title={<FormattedMessage id="Product Catalog" />} size="small" style={{ borderRadius: 8 }}>
              <div className="section_toggle_row" style={{ marginBottom: catalogEnabled ? 16 : 0 }}>
                <div className="section_toggle_label">
                  <p className="title">
                    <FormattedMessage id="Enable catalog sync" />
                  </p>
                  <p className="description">
                    <FormattedMessage id="Publishes the product catalog to Meta Commerce Manager." />
                  </p>
                </div>
                <Switch id="integration-meta-catalog-enabled" checked={catalogEnabled} onChange={setCatalogEnabled} />
              </div>

              {catalogEnabled && (
                <Form layout="vertical" colon={false}>
                  <Form.Item
                    label={labelOf('Catalog ID')}
                    extra={<FormattedMessage id="Catalog identifier in Meta Commerce Manager." />}
                    validateStatus={errors.catalogId ? 'error' : ''}
                    help={errors.catalogId}
                    style={{ marginBottom: 14 }}
                  >
                    <Input
                      id="integration-meta-catalog-id"
                      value={catalog.catalogId}
                      placeholder="936041368829591"
                      onChange={e => handleCatalogChange('catalogId', e.target.value)}
                    />
                  </Form.Item>

                  <Form.Item
                    label={labelOf('Image Base URL')}
                    extra={
                      <FormattedMessage id="Base used to build product image URLs. Changing it affects every published product — coordinate with backend first." />
                    }
                    validateStatus={errors.imageBaseUrl ? 'error' : ''}
                    help={errors.imageBaseUrl}
                    style={{ marginBottom: 14 }}
                  >
                    <Input
                      id="integration-meta-image-base-url"
                      value={catalog.imageBaseUrl}
                      placeholder="https://s3-us-west-1.amazonaws.com/.../customer/"
                      onChange={e => handleCatalogChange('imageBaseUrl', e.target.value)}
                    />
                  </Form.Item>

                  <Form.Item
                    label={labelOf('Product Link Base URL')}
                    extra={<FormattedMessage id="Public storefront domain used as the base of catalog deep links." />}
                    validateStatus={errors.productLinkBaseUrl ? 'error' : ''}
                    help={errors.productLinkBaseUrl}
                    style={{ marginBottom: 14 }}
                  >
                    <Input
                      id="integration-meta-product-link-base-url"
                      value={catalog.productLinkBaseUrl}
                      placeholder="https://pwa.mcdonaldsapis.com"
                      onChange={e => handleCatalogChange('productLinkBaseUrl', e.target.value)}
                    />
                  </Form.Item>

                  <Form.Item
                    label={labelOf('Sync interval')}
                    extra={<FormattedMessage id="How often the automatic delta sync runs." />}
                    validateStatus={errors.syncIntervalMinutes ? 'error' : ''}
                    help={errors.syncIntervalMinutes}
                    style={{ marginBottom: 14 }}
                  >
                    <InputNumber
                      id="integration-meta-sync-interval"
                      min={1}
                      max={1440}
                      precision={0}
                      value={catalog.syncIntervalMinutes}
                      onChange={(v: number | null) =>
                        handleCatalogChange('syncIntervalMinutes', v ?? DEFAULT_CATALOG.syncIntervalMinutes)
                      }
                      style={{ width: '100%' }}
                      addonAfter={<FormattedMessage id="min" />}
                    />
                  </Form.Item>

                  <Form.Item
                    label={labelOf('Graph API version')}
                    extra={<FormattedMessage id="Meta Graph API version. Normally left as is." />}
                    validateStatus={errors.graphApiVersion ? 'error' : ''}
                    help={errors.graphApiVersion}
                    style={{ marginBottom: 14 }}
                  >
                    <Input
                      id="integration-meta-graph-api-version"
                      value={catalog.graphApiVersion}
                      placeholder={DEFAULT_CATALOG.graphApiVersion}
                      onChange={e => handleCatalogChange('graphApiVersion', e.target.value)}
                    />
                  </Form.Item>

                  <Form.Item
                    htmlFor="integration-meta-catalog-access-token"
                    label={tokenLabel(META_TOKEN_KEYS.catalog)}
                    extra={
                      <FormattedMessage id="System user token used to publish the catalog. Stored encrypted; leave empty to keep the current one." />
                    }
                    validateStatus={errors[META_TOKEN_KEYS.catalog] ? 'error' : ''}
                    help={errors[META_TOKEN_KEYS.catalog]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input.Password
                      id="integration-meta-catalog-access-token"
                      value={tokens[META_TOKEN_KEYS.catalog]}
                      autoComplete="new-password"
                      placeholder={tokenPlaceholder(META_TOKEN_KEYS.catalog)}
                      onChange={e => handleTokenChange(META_TOKEN_KEYS.catalog, e.target.value)}
                    />
                  </Form.Item>
                </Form>
              )}
            </Card>

            <Card title={<FormattedMessage id="Synchronization status" />} size="small" style={{ borderRadius: 8 }}>
              <div className="meta_status_grid">
                <div className="meta_status_item">
                  <p className="description">
                    <FormattedMessage id="Last synchronization" />
                  </p>
                  <p className="title">{formatDateTime(metaStatus?.lastSyncAt)}</p>
                </div>
                {lastReport && (
                  <>
                    <div className="meta_status_item">
                      <p className="description">
                        <FormattedMessage id="Mode" />
                      </p>
                      <p className="title">
                        <FormattedMessage id={lastReport.mode === 'full' ? 'Full' : 'Delta'} />
                      </p>
                    </div>
                    <div className="meta_status_item">
                      <p className="description">
                        <FormattedMessage id="Products updated" />
                      </p>
                      <p className="title">{lastReport.upserted}</p>
                    </div>
                    <div className="meta_status_item">
                      <p className="description">
                        <FormattedMessage id="Products removed" />
                      </p>
                      <p className="title">{lastReport.deleted}</p>
                    </div>
                    {reportDuration && (
                      <div className="meta_status_item">
                        <p className="description">
                          <FormattedMessage id="Duration" />
                        </p>
                        <p className="title">{reportDuration}</p>
                      </div>
                    )}
                  </>
                )}
              </div>

              {lastReport?.error && (
                <div className="meta_warning_box meta_warning_box--error">
                  <WarningOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                  <span>{lastReport.error}</span>
                </div>
              )}

              {itemErrors.length > 0 && (
                <Collapse
                  ghost
                  className="meta_errors_collapse"
                  activeKey={showErrors ? ['errors'] : []}
                  onChange={keys => setShowErrors((keys as string[]).length > 0)}
                  items={[
                    {
                      key: 'errors',
                      label: (
                        <span className="meta_errors_header">
                          <Tag color="error" style={{ margin: 0 }}>
                            {itemErrors.length}
                          </Tag>
                          <FormattedMessage id="products failed to sync" />
                        </span>
                      ),
                      children: (
                        <Table
                          rowKey={(row, index) => `${row.externalId}-${index}`}
                          size="small"
                          pagination={itemErrors.length > 10 ? { pageSize: 10, size: 'small' } : false}
                          dataSource={itemErrors}
                          columns={[
                            {
                              title: <FormattedMessage id="Product ID" />,
                              dataIndex: 'externalId',
                              width: 120,
                            },
                            {
                              title: <FormattedMessage id="Error" />,
                              dataIndex: 'message',
                            },
                          ]}
                        />
                      ),
                    },
                  ]}
                />
              )}

              <p className="description meta_hint" style={{ marginTop: 12 }}>
                <FormattedMessage id="The catalog also syncs on its own: a delta run on the configured interval and a full run at 3 AM." />
              </p>

              <Tooltip title={syncBlockedReason}>
                <div className="meta_sync_actions">
                  <Button
                    id="integration-meta-sync-delta"
                    className="btn_clear"
                    disabled={isDirty || isSyncing || syncCooldown || !catalogOperating}
                    onClick={() => runSync('delta')}
                  >
                    <SyncOutlined
                      spin={isSyncing}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                    <FormattedMessage id="Sync changes now" />
                  </Button>
                  <Button
                    id="integration-meta-sync-full"
                    className="btn_clear"
                    disabled={isDirty || isSyncing || syncCooldown || !catalogOperating}
                    onClick={() => runSync('full')}
                  >
                    <SyncOutlined
                      spin={isSyncing}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                    <FormattedMessage id="Full synchronization" />
                  </Button>
                </div>
              </Tooltip>
              {isSyncing && (
                <p className="description meta_hint">
                  <FormattedMessage id="Synchronizing… this can take up to a minute, please do not close the page." />
                </p>
              )}
              {!catalogOperating && !isDirty && (
                <p className="description meta_hint meta_hint--warning">
                  <FormattedMessage id="The catalog channel is not operating, so manual synchronization is unavailable." />
                  {catalogReason && <> {catalogReason}</>}
                </p>
              )}
            </Card>
          </div>

          <div className="integrations_right_col">
            <Card title={<FormattedMessage id="Conversion Events" />} size="small" style={{ borderRadius: 8 }}>
              <div className="section_toggle_row" style={{ marginBottom: capiEnabled ? 16 : 0 }}>
                <div className="section_toggle_label">
                  <p className="title">
                    <FormattedMessage id="Enable conversion events" />
                  </p>
                  <p className="description">
                    <FormattedMessage id="Server-side reporting of Purchase, AddToCart and ViewContent events to Meta." />
                  </p>
                </div>
                <Switch id="integration-meta-capi-enabled" checked={capiEnabled} onChange={setCapiEnabled} />
              </div>

              {capiEnabled && (
                <Form layout="vertical" colon={false}>
                  <Form.Item
                    label={labelOf('Pixel ID')}
                    extra={
                      <FormattedMessage id="Meta dataset/pixel identifier for this country. It is a public identifier, also used by the storefront." />
                    }
                    validateStatus={errors.pixelId ? 'error' : ''}
                    help={errors.pixelId}
                    style={{ marginBottom: 14 }}
                  >
                    <Input
                      id="integration-meta-pixel-id"
                      value={capi.pixelId}
                      placeholder="2363043137558215"
                      onChange={e => handleCapiChange('pixelId', e.target.value)}
                    />
                  </Form.Item>

                  <Form.Item
                    label={labelOf('Test event code')}
                    extra={
                      <FormattedMessage id="Leave empty in production. Meta only shows these events under Test Events." />
                    }
                    style={{ marginBottom: 14 }}
                  >
                    <Input
                      id="integration-meta-test-event-code"
                      value={capi.testEventCode}
                      placeholder={t('Empty — events count as real')}
                      onChange={e => handleCapiChange('testEventCode', e.target.value)}
                    />
                  </Form.Item>

                  <Form.Item
                    htmlFor="integration-meta-capi-access-token"
                    label={tokenLabel(META_TOKEN_KEYS.capi)}
                    extra={
                      <FormattedMessage id="Conversions API token used to report events. Stored encrypted; leave empty to keep the current one." />
                    }
                    validateStatus={errors[META_TOKEN_KEYS.capi] ? 'error' : ''}
                    help={errors[META_TOKEN_KEYS.capi]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input.Password
                      id="integration-meta-capi-access-token"
                      value={tokens[META_TOKEN_KEYS.capi]}
                      autoComplete="new-password"
                      placeholder={tokenPlaceholder(META_TOKEN_KEYS.capi)}
                      onChange={e => handleTokenChange(META_TOKEN_KEYS.capi, e.target.value)}
                    />
                  </Form.Item>

                  {Boolean(capi.testEventCode.trim()) && (
                    <div className="meta_warning_box">
                      <WarningOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                      <span>
                        <FormattedMessage id="Test mode active — events are not counted in real metrics." />
                      </span>
                    </div>
                  )}
                </Form>
              )}
            </Card>

            <Card
              title={<FormattedMessage id="What the storefront is receiving" />}
              size="small"
              style={{ borderRadius: 8 }}
            >
              <p className="description" style={{ marginBottom: 12 }}>
                <FormattedMessage id="Live values the web and app are reading right now — confirms the effect of saved changes." />
              </p>
              <div className="meta_status_grid">
                <div className="meta_status_item">
                  <p className="description">
                    <FormattedMessage id="Conversion Events" />
                  </p>
                  <Tag color={storefrontTracking ? 'success' : 'error'} style={{ margin: 0, width: 'fit-content' }}>
                    <FormattedMessage id={storefrontTracking ? 'Active' : 'Inactive'} />
                  </Tag>
                </div>
                <div className="meta_status_item">
                  <p className="description">
                    <FormattedMessage id="Pixel ID" />
                  </p>
                  <p className="title">{publicConfig?.pixelId || '—'}</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      <div className="integrations_actions">
        <Button id="integration-meta-save" onClick={onSave}>
          <FormattedMessage id="Save" />
          <ArrowForward />
        </Button>
        <p id="integration-meta-cancel" className="integrations_cancel_btn" onClick={onCancel}>
          <FormattedMessage id="Cancel" />
        </p>
      </div>
    </div>
  );
};

export default MetaConfig;
